import { LegendStates, MouseSelectionStates } from "types";
import { SeriesType } from "types/echarts";
import { allTrue } from "utils/fp";

import {
  UNDEFINED_DATA_POINT,
  getPointCoordinates,
  getSeriesName,
  isValidChartLegendStates
} from "./utils";

export function crossPlotHoverLegends({
  params,
  series,
  response
}: Partial<MouseSelectionStates>) {
  // check if series is undefined or if params.seriesIndex is undefined
  if (params.seriesIndex === undefined || !series)
    return { hoverLegendItem: undefined, hoverLegendGroup: undefined };

  // if series is scatter, check if params.dataIndex is undefined
  if (
    series[params.seriesIndex].type === SeriesType.Scatter &&
    params.dataIndex === undefined
  )
    return { hoverLegendItem: undefined, hoverLegendGroup: undefined };
  // The data for each point in the series is stored sequentially as [x, y, wellIndex].
  // We compute the index for wellIndex by multiplying the dataIndex by 3 (since there are three entries per point)
  // and adding 2 (the third element in the triple).
  let wellName = "";
  if (series[params.seriesIndex].type === SeriesType.Scatter) {
    //contains the index to the related UWI
    // Safety check: ensure params.data exists and has at least 3 elements
    if (params.data && Array.isArray(params.data) && params.data.length >= 3) {
      const wellIndex = params.data[2];
      wellName = response?.uwis[wellIndex] ?? "";
    }
  } else {
    wellName = String(series[params.seriesIndex]?.name);
  }
  const seriesName = params.seriesName || getSeriesName({ params, series });

  // Notes:
  // For focus fields, hoverLegendItem is expected to represent the group name for highlighting.
  // Currently, hovering over data points on the cross plot or  rate cum at spaghetti does not highlight the corresponding bins.
  // We have maintained this behavior in the new chart. To enable highlighting, change hoverLegendItem to seriesName.
  return {
    hoverLegendItem: wellName,
    hoverLegendGroup: seriesName
  };
}

export function crossPlotSelectionParams({ states, series }: Partial<LegendStates>) {
  const { hoverLegendGroup, hoverLegendItem, attentionWells, syncWells } = states;

  const isInvalid = isValidChartLegendStates({ states, series });
  if (isInvalid) return UNDEFINED_DATA_POINT;

  const isHoveringOnChart = allTrue(
    hoverLegendItem?.length > 0,
    hoverLegendGroup?.length > 0,
    hoverLegendGroup !== hoverLegendItem
  );

  const isHoveringOnMap = allTrue(
    hoverLegendItem?.length > 0,
    !hoverLegendGroup,
    attentionWells?.length > 0
  );

  const isSyncingToWell =
    !hoverLegendItem && syncWells?.isSync && syncWells?.activeWells?.length > 0;

  const conditions = [
    {
      predicate: () => isHoveringOnChart,
      transform: () => {
        const candidate = getPointCoordinates(states, hoverLegendItem);
        return candidate;
      }
    },
    {
      predicate: () => isHoveringOnMap,
      transform: () => {
        const candidate = getPointCoordinates(states, attentionWells[0]);
        return candidate;
      }
    },
    {
      predicate: () => isSyncingToWell,
      transform: () => {
        const candidate = getPointCoordinates(states, syncWells.activeWells[0]);
        return candidate;
      }
    }
  ];

  const result = conditions.find((condition) => condition.predicate())?.transform();

  return result ?? UNDEFINED_DATA_POINT;
}
